'use client';

import { useQuery } from '@tanstack/react-query';
import type { ReactNode} from 'react';
import { Fragment, useEffect, useState } from 'react';

import { ArrowLeft, Author, Banner, Brand } from '@zoomit/icons';
import { Box, Col, Flex, Link, Skeleton } from '@zoomit/primitives';
import type {
  AuthorType,
  InternalArticleCardResponse,
  SearchResultKeys,
  SearchResultType} from '@zoomit/web-kit';
import {
  isMobile,
  NoResult,
  onSearchResultClick,
  searchApi,
  SearchCategory,
  SearchNoResultDescription
} from '@zoomit/web-kit';
import { Button, cssVar, List, ListItem, RedDot, TextIcon, theme, Typography } from '@zoomit/zoomit-ds';

type Props = {
  searchQuery: string;
  onClose?: () => void;
};

const resultContents: {
  title: string;
  value: SearchResultKeys;
  icon: (color: string) => ReactNode;
  maxCount: number;
}[] = [
  {
    title: 'دسته مطالب',
    value: 'groupings',
    icon: (color) => <Brand fill={color} />,
    maxCount: 3,
  },
  {
    title: 'مطالب',
    value: 'articles',
    icon: (color) => <Banner fill={color} />,
    maxCount: 8,
  },
  {
    title: 'نویسندگان',
    value: 'authors',
    icon: (color) => <Author fill={color} />,
    maxCount: 2,
  },
];

export const SearchInstance = (props: Props) => {
  const { searchQuery, onClose } = props;

  const [selectedCategory, setSelectedCategory] = useState('groupings');

  const { isLoading, data: searchResult } = useQuery(
    ['search', searchQuery],
    async () => {
      const res = await searchApi({
        query: searchQuery,
        articlesPageSize: 8,
        groupingsPageSize: 3,
        authorsPageSize: 2,
      });

      return res.data;
    },
    { enabled: searchQuery.length !== 0 },
  );

  const shouldRenderSearchCategory = isMobile && searchQuery.length > 0 && !isLoading;

  const canRender = (category: keyof SearchResultType) => {
    if (isMobile) {
      if (selectedCategory === category)
        return isLoading || searchResult === undefined || searchResult[category].source.length > 0;
      if (selectedCategory !== 'all') return false;
    }
    return isLoading || searchResult === undefined || searchResult[category].source.length > 0;
  };

  const eachCategoryResultLength = {
    articles: searchResult?.articles.source.length || 0,
    authors: searchResult?.authors.source.length || 0,
    groupings: searchResult?.groupings.source.length || 0,
  };

  const isEmpty = !isLoading && Object.values(eachCategoryResultLength).every((count) => count === 0);

  useEffect(() => {
    if (isMobile) {
      if (searchResult) {
        const firstCategory = (Object.keys(eachCategoryResultLength) as (keyof SearchResultType)[]).find(
          (category) => {
            return searchResult[category].source.length > 0;
          },
        );
        firstCategory && setSelectedCategory(firstCategory);
      } else {
        setSelectedCategory('all');
      }
    }
  }, [searchResult]);

  const contentWrapperClasses = 'mt-4 flex min-h-8 flex-col gap-4 md:flex-row';
  const titleWrapperClasses = 'mt-[5px] justify-start items-center';

  const renderResults = ({
    title,
    icon,
    value,
    maxCount,
  }: {
    title: string;
    value: SearchResultKeys;
    icon: (color: string) => ReactNode;
    maxCount: number;
  }) => {
    return canRender(value) ? (
      <Fragment key={value}>
        <div className={contentWrapperClasses}>
          <Col md={2} size={12}>
            <TextIcon className={titleWrapperClasses}>
              {icon(isMobile ? cssVar(theme.primary1) : cssVar(theme.text))}
              <Typography
                variant="subhead-bold"
                textColor={isMobile ? cssVar(theme.primary2) : cssVar(theme.text)}
              >
                {isMobile ? `${title} یافت شده با عبارت “ ${searchQuery} “` : title}
              </Typography>
            </TextIcon>
          </Col>
          <Col md={10} size={12}>
            <List>
              {isLoading || searchResult === undefined
                ? Array.from({ length: maxCount }).map((_, index) => (
                    <ListItem indicator={<RedDot />} key={index} spacing={{ mb: 'x2' }}>
                      <Skeleton variant="text" width={12} />
                    </ListItem>
                  ))
                : searchResult[value].source.map((item, index) => {
                    const { fullName, userId } = item as AuthorType;
                    const { title, slug } = item as InternalArticleCardResponse;
                    return (
                      <ListItem
                        indicator={<RedDot />}
                        key={index}
                        spacing={{ mb: 'x2' }}
                        onClick={() =>
                          onSearchResultClick({
                            title: title || fullName,
                            section: value,
                          })
                        }
                      >
                        <Link
                          href={slug ? `/${slug}` : `/author/${userId}`}
                          className="**:hover:text-text-hover"
                        >
                          <Typography onClick={() => onClose?.()} variant="callout" lineHeight={1.6}>
                            {title || fullName}
                          </Typography>
                        </Link>
                      </ListItem>
                    );
                  })}
            </List>
          </Col>
        </div>
        {!isMobile && <div className="bg-borderPrimary mt-3 h-px w-full last:hidden" />}
      </Fragment>
    ) : null;
  };

  return (
    <Box className={'overflow-hidden'}>
      {shouldRenderSearchCategory && (
        <Box spacing={{ pb: 'x2' }}>
          <SearchCategory
            counts={eachCategoryResultLength}
            categories={resultContents}
            selectedCategory={selectedCategory}
            handleChangeCategory={setSelectedCategory}
          />
        </Box>
      )}
      {searchQuery.length > 0 && (
        <>
          <div
            className={
              'no-scrollbar max-h-[calc(100vh-220px)] overflow-x-hidden overflow-y-auto pt-2 md:max-h-[70%]'
            }
          >
            {isEmpty && (
              <Flex justifyContent="center">
                <NoResult description={SearchNoResultDescription} />
              </Flex>
            )}
            {resultContents.map((content) => {
              if (searchResult?.[content.value].source.length === 0) {
                return null;
              }
              return renderResults(content);
            })}
          </div>
          {!isLoading && searchQuery && !isEmpty && (
            <Link href={`/search/${searchQuery}`}>
              <Button
                spacing={{ mt: 'x6' }}
                variant={isMobile ? 'outline' : 'text'}
                size={isMobile ? 'full width' : 'fit'}
                leftIcon={<ArrowLeft size="sm" className="!fill-primary1" />}
              >
                مشاهده تمامی نتایج
              </Button>
            </Link>
          )}
        </>
      )}
    </Box>
  );
};
